import { supabase } from "@/lib/supabase";
import type { Message, MessageThread, Order } from "@/types";
import { useCallback, useEffect, useState } from "react";

interface UseThreadsOptions {
	storeId?: string;
	status?: string;
	page?: number;
	pageSize?: number;
}

export type ThreadOrderSummary = Pick<
	Order,
	| "id"
	| "status"
	| "shopify_order_id"
	| "shopify_order_number"
	| "confidence"
	| "notes"
	| "error_message"
	| "items"
	| "total_amount"
	| "created_at"
>;

export type ThreadMessageSummary = Pick<
	Message,
	| "id"
	| "store_id"
	| "thread_id"
	| "external_message_id"
	| "sender_email"
	| "sender_name"
	| "subject"
	| "body_text"
	| "body_html"
	| "raw_payload"
	| "status"
	| "gemini_analysis"
	| "last_error"
	| "retry_count"
	| "last_attempted_at"
	| "created_at"
> & {
	orders?: ThreadOrderSummary[];
};

export type ThreadWithRelations = MessageThread & {
	store?: {
		id: string;
		name: string;
		shopify_domain: string;
	};
	messages?: ThreadMessageSummary[];
	latestMessage: ThreadMessageSummary | null;
	latestInboundMessage: ThreadMessageSummary | null;
};

export function useThreads(options: UseThreadsOptions = {}) {
	const { storeId, status, page = 1, pageSize = 25 } = options;
	const [threads, setThreads] = useState<ThreadWithRelations[]>([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const fetchThreads = useCallback(async () => {
		setLoading(true);
		setError(null);

		let query = supabase
			.from("message_threads")
			.select(
				"*, store:stores(id, name, shopify_domain), messages(*, orders(id, status, shopify_order_id, shopify_order_number, confidence, notes, error_message, items, total_amount, created_at))",
				{ count: "exact" },
			)
			.order("updated_at", { ascending: false })
			.range((page - 1) * pageSize, page * pageSize - 1);

		if (storeId) {
			query = query.eq("store_id", storeId);
		}

		if (status) {
			query = query.eq("messages.status", status);
		}

		const { data, error: err, count } = await query;

		if (err) {
			setError(err.message);
			setLoading(false);
			return;
		}

		const typedThreads = ((data as ThreadWithRelations[] | null) ?? [])
			.map((thread) => {
				const messages = [...(thread.messages ?? [])].sort((a, b) => {
					return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
				});

				return {
					...thread,
					messages,
					latestMessage: messages[0] ?? null,
					latestInboundMessage: messages[0] ?? null,
				};
			})
			.filter((thread) => {
				if (!status) {
					return true;
				}

				return thread.messages?.some((message) => message.status === status) ?? false;
			});

		setThreads(typedThreads);
		setTotal(count ?? typedThreads.length);
		setLoading(false);
	}, [page, pageSize, status, storeId]);

	useEffect(() => {
		void fetchThreads();
	}, [fetchThreads]);

	return { threads, total, loading, error, refetch: fetchThreads };
}
